import _ from 'lodash';
import { Draft } from 'immer';
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { stringsEqualIgnoreCase } from '../util';
import { purgeScreenFromMemory } from '../screenMemory';
import { nowMillisecond } from '../dateUtils';

export interface ScreenStackItem {
  url: string,
  parentUrl: string|null,
  metadata: any|null,
  timestamp: string,
  afterScreenUrl: string|null,
};

interface MoveScreenToOperation {
  url: string,
  sourceOperation: string,
  destinationOperation: string,
};

interface PushScreenToStack {
  url: string,
  parentUrl: string|null,
  operation?: string|null,
  activeOperation?: string|null,
  metadata: any|null,
  afterScreenUrl: string|null,
};

interface ReplaceUrlOnStack {
  oldUrl: string,
  newUrl: string,
  metadata: any|null,
};

interface ReplaceScreenStackMetadata {
  url: string,
  metadata: any|null,
};

export interface ScreenStackState {
  [key: string]: Array<ScreenStackItem>,
  home: Array<ScreenStackItem>,
  oceanImport: Array<ScreenStackItem>,
  oceanExport: Array<ScreenStackItem>,
  airImport: Array<ScreenStackItem>,
  airExport: Array<ScreenStackItem>,
  otherShipment: Array<ScreenStackItem>,
  accounting: Array<ScreenStackItem>,
  warehousing: Array<ScreenStackItem>,
  whReceipt: Array<ScreenStackItem>,
  customer: Array<ScreenStackItem>,
  standalone: Array<ScreenStackItem>,
  opReport: Array<ScreenStackItem>,
  utility: Array<ScreenStackItem>,
  billings: Array<ScreenStackItem>,
  myEvent: Array<ScreenStackItem>,
  businessIntell: Array<ScreenStackItem>,
  ams: Array<ScreenStackItem>,
  isf: Array<ScreenStackItem>,
  aes: Array<ScreenStackItem>,
  myTask: Array<ScreenStackItem>,
  shipmentStatus: Array<ScreenStackItem>,
  aiCenter: Array<ScreenStackItem>,
};

interface PopScreenFromStack {
  url: string,
  activeOperation: string|null,
};

interface MoveScreenToTop {
  url: string,
  operation: string,
};

interface ReplaceUrlParentOpOnStack {
  url: string,
  metadata: any|null,
};

const initialState:ScreenStackState = {
  home: [],
  oceanImport: [],
  oceanExport: [],
  airImport: [],
  airExport: [],
  otherShipment: [],
  accounting: [],
  warehousing: [],
  customer: [],
  standalone: [],
  whReceipt: [],
  opReport: [],
  utility: [],
  billings: [],
  myEvent: [],
  businessIntell: [],
  ams: [],
  isf: [],
  aes: [],
  myTask: [],
  shipmentStatus: [],
  cntr : [],
  aiCenter: [],
};

function doMoveScreenToOperation(currentState: Draft<ScreenStackState>, url: string, sourceOperation: string, destinationOperation: string) {
  if (sourceOperation === destinationOperation) {
    return;
  }

  let sourceSection = currentState[sourceOperation];
  let destinationSection = currentState[destinationOperation];

  const moveItemIndex = sourceSection.findIndex(x => x.url === url);
  if (moveItemIndex >= 0) {
    const moveItems = sourceSection.splice(moveItemIndex, 1);
    destinationSection.push(moveItems[0]);
  }
}

function moveScreenToTopForSection(section:Draft<Array<ScreenStackItem>>, url:string) {
  if (section.length <= 0 || section[section.length - 1].url === url) {
    // Nothing to do. Either there is no screens on stack or screen with url is already on top
    return;
  }

  const currentIndex = section.findIndex(x => x.url === url);
  if (currentIndex < 0) {
    return;
  }

  const moveItem = section.splice(currentIndex, 1)[0];
  moveItem.timestamp = nowMillisecond();

  section.push(moveItem);
}

function pushToStackForSection(section:Draft<Array<ScreenStackItem>>, url:string, parentUrl:string|null, metadata:any|null, afterScreenUrl: string|null) {
  const existingIndex = section.findIndex(x => stringsEqualIgnoreCase(x.url, url));
  let currentTime = nowMillisecond();

  
  if (existingIndex >= 0) {
    section[existingIndex].url = url;
    section[existingIndex].parentUrl = parentUrl;
    section[existingIndex].metadata = metadata;
    section[existingIndex].timestamp = currentTime;
    section[existingIndex].afterScreenUrl = afterScreenUrl;
  } else {
    section.push({url, parentUrl, metadata, timestamp: currentTime, afterScreenUrl});
  }
}

function pushToStack(currentState:Draft<ScreenStackState>, actionPayload:PushScreenToStack) {
  if (!actionPayload.activeOperation) {
    throw new Error('Missing activeOperation parameter');
  }

  pushToStackForSection(currentState[actionPayload.activeOperation], actionPayload.url, actionPayload.parentUrl, actionPayload.metadata, actionPayload.afterScreenUrl);
}

function popScreenFromSection(section:Draft<Array<ScreenStackItem>>, urlToRemove:string) {
  const urlsToDelete:Array<string> = [];
  let query = [urlToRemove];

  //  Build dependency tree of urls to delete
  while (query && query.length > 0) {
    urlsToDelete.unshift(...query);
    // eslint-disable-next-line no-loop-func
    query = section.filter(s => query.some(q => stringsEqualIgnoreCase(q, s.parentUrl))).map(x => x.url);
  }

  const deleteIndices = Array.from(new Set( // to remove duplicates
      section.reduce((a, e, i) => {
      if (urlsToDelete.some(d => stringsEqualIgnoreCase(d, e.url))) {
        a.push(i);
      }
      return a;
    }, [] as number[])
  ));
  deleteIndices.sort((a, b) => b-a); // Sort descending

  deleteIndices.forEach(i => section.splice(i , 1));

  purgeScreenFromMemory(urlsToDelete);
}

function replaceUrlOnStack(currentState:Draft<ScreenStackState>, actionPayload:ReplaceUrlOnStack) {
  purgeScreenFromMemory([actionPayload.oldUrl]);
  const currentTime = nowMillisecond();

  for (let i = 0; i < currentState.oceanImport.length; ++i) {
    if (currentState.oceanImport[i].url === actionPayload.oldUrl) {
      currentState.oceanImport[i].url = actionPayload.newUrl;
      currentState.oceanImport[i].metadata = actionPayload.metadata;
      currentState.oceanImport[i].timestamp = currentTime;
    }
  }

  for (let i = 0; i < currentState.oceanExport.length; ++i) {
    if (currentState.oceanExport[i].url === actionPayload.oldUrl) {
      currentState.oceanExport[i].url = actionPayload.newUrl;
      currentState.oceanExport[i].metadata = actionPayload.metadata;
      currentState.oceanExport[i].timestamp = currentTime;
    }
  }

  for (let i = 0; i < currentState.airImport.length; ++i) {
    if (currentState.airImport[i].url === actionPayload.oldUrl) {
      currentState.airImport[i].url = actionPayload.newUrl;
      currentState.airImport[i].metadata = actionPayload.metadata;
      currentState.airImport[i].timestamp = currentTime;
    }
  }

  for (let i = 0; i < currentState.airExport.length; ++i) {
    if (currentState.airExport[i].url === actionPayload.oldUrl) {
      currentState.airExport[i].url = actionPayload.newUrl;
      currentState.airExport[i].metadata = actionPayload.metadata;
      currentState.airExport[i].timestamp = currentTime;
    }
  }

  for (let i = 0; i < currentState.otherShipment.length; ++i) {
    if (currentState.otherShipment[i].url === actionPayload.oldUrl) {
      currentState.otherShipment[i].url = actionPayload.newUrl;
      currentState.otherShipment[i].metadata = actionPayload.metadata;
      currentState.otherShipment[i].timestamp = currentTime;
    }
  }

  for (let i = 0; i < currentState.accounting.length; ++i) {
    if (currentState.accounting[i].url === actionPayload.oldUrl) {
      currentState.accounting[i].url = actionPayload.newUrl;
      currentState.accounting[i].metadata = actionPayload.metadata;
      currentState.accounting[i].timestamp = currentTime;
    }
  }

  for (let i = 0; i < currentState.whReceipt.length; ++i) {
    if (currentState.whReceipt[i].url === actionPayload.oldUrl) {
      currentState.whReceipt[i].url = actionPayload.newUrl;
      currentState.whReceipt[i].metadata = actionPayload.metadata;
      currentState.whReceipt[i].timestamp = currentTime;
    }
  }

  for (let i = 0; i < currentState.warehousing.length; ++i) {
    if (currentState.warehousing[i].url === actionPayload.oldUrl) {
      currentState.warehousing[i].url = actionPayload.newUrl;
      currentState.warehousing[i].metadata = actionPayload.metadata;
      currentState.warehousing[i].timestamp = currentTime;
    }
  }

  for (let i = 0; i < currentState.customer.length; ++i) {
    if (currentState.customer[i].url === actionPayload.oldUrl) {
      currentState.customer[i].url = actionPayload.newUrl;
      currentState.customer[i].metadata = actionPayload.metadata;
      currentState.customer[i].timestamp = currentTime;
    }
  }

  for (let i = 0; i < currentState.utility.length; ++i) {
    if (currentState.utility[i].url === actionPayload.oldUrl) {
      currentState.utility[i].url = actionPayload.newUrl;
      currentState.utility[i].metadata = actionPayload.metadata;
      currentState.utility[i].timestamp = currentTime;
    }
  }
  for (let i = 0; i < currentState.billings.length; ++i) {
    if (currentState.billings[i].url === actionPayload.oldUrl) {
      currentState.billings[i].url = actionPayload.newUrl;
      currentState.billings[i].metadata = actionPayload.metadata;
      currentState.billings[i].timestamp = currentTime;
    }
  }
  for (let i = 0; i < currentState.myEvent.length; ++i) {
    if (currentState.myEvent[i].url === actionPayload.oldUrl) {
      currentState.myEvent[i].url = actionPayload.newUrl;
      currentState.myEvent[i].metadata = actionPayload.metadata;
      currentState.myEvent[i].timestamp = currentTime;
    }
  }
  for (let i = 0; i < currentState.businessIntell.length; ++i) {
    if (currentState.businessIntell[i].url === actionPayload.oldUrl) {
      currentState.businessIntell[i].url = actionPayload.newUrl;
      currentState.businessIntell[i].metadata = actionPayload.metadata;
      currentState.businessIntell[i].timestamp = currentTime;
    }
  }
  for (let i = 0; i < currentState.ams.length; ++i) {
    if (currentState.ams[i].url === actionPayload.oldUrl) {
      currentState.ams[i].url = actionPayload.newUrl;
      currentState.ams[i].metadata = actionPayload.metadata;
      currentState.ams[i].timestamp = currentTime;
    }
  }
  for (let i = 0; i < currentState.isf.length; ++i) {
    if (currentState.isf[i].url === actionPayload.oldUrl) {
      currentState.isf[i].url = actionPayload.newUrl;
      currentState.isf[i].metadata = actionPayload.metadata;
      currentState.isf[i].timestamp = currentTime;
    }
  }
  for (let i = 0; i < currentState.aes.length; ++i) {
    if (currentState.aes[i].url === actionPayload.oldUrl) {
      currentState.aes[i].url = actionPayload.newUrl;
      currentState.aes[i].metadata = actionPayload.metadata;
      currentState.aes[i].timestamp = currentTime;
    }
  }
  for (let i = 0; i < currentState.myTask.length; ++i) {
    if (currentState.myTask[i].url === actionPayload.oldUrl) {
      currentState.myTask[i].url = actionPayload.newUrl;
      currentState.myTask[i].metadata = actionPayload.metadata;
      currentState.myTask[i].timestamp = currentTime;
    }
  }

  for (let i = 0; i < currentState.cntr.length; ++i) {
    if (currentState.cntr[i].url === actionPayload.oldUrl) {
      currentState.cntr[i].url = actionPayload.newUrl;
      currentState.cntr[i].metadata = actionPayload.metadata;
      currentState.cntr[i].timestamp = currentTime;
    }
  }

  for (let i = 0; i < currentState.shipmentStatus.length; ++i) {
    if (currentState.shipmentStatus[i].url === actionPayload.oldUrl) {
      currentState.shipmentStatus[i].url = actionPayload.newUrl;
      currentState.shipmentStatus[i].metadata = actionPayload.metadata;
      currentState.shipmentStatus[i].timestamp = currentTime;
    }
  }

  for (let i = 0; i < currentState.aiCenter.length; ++i) {
    if (currentState.aiCenter[i].url === actionPayload.oldUrl) {
      currentState.aiCenter[i].url = actionPayload.newUrl;
      currentState.aiCenter[i].metadata = actionPayload.metadata;
      currentState.aiCenter[i].timestamp = currentTime;
    }
  }

}


function replaceUrlMetadata(currentState:Draft<ScreenStackState>, actionPayload:ReplaceScreenStackMetadata) {
  for (let i = 0; i < currentState.oceanImport.length; ++i) {
    if (currentState.oceanImport[i].url === actionPayload.url) {
      currentState.oceanImport[i].metadata = actionPayload.metadata;
    }
  }

  for (let i = 0; i < currentState.oceanExport.length; ++i) {
    if (currentState.oceanExport[i].url === actionPayload.url) {
      currentState.oceanExport[i].metadata = actionPayload.metadata;
    }
  }

  for (let i = 0; i < currentState.airImport.length; ++i) {
    if (currentState.airImport[i].url === actionPayload.url) {
      currentState.airImport[i].metadata = actionPayload.metadata;
    }
  }

  for (let i = 0; i < currentState.airExport.length; ++i) {
    if (currentState.airExport[i].url === actionPayload.url) {
      currentState.airExport[i].metadata = actionPayload.metadata;
    }
  }

  for (let i = 0; i < currentState.otherShipment.length; ++i) {
    if (currentState.otherShipment[i].url === actionPayload.url) {
      currentState.otherShipment[i].metadata = actionPayload.metadata;
    }
  }

  for (let i = 0; i < currentState.accounting.length; ++i) {
    if (currentState.accounting[i].url === actionPayload.url) {
      currentState.accounting[i].metadata = actionPayload.metadata;
    }
  }

  for (let i = 0; i < currentState.warehousing.length; ++i) {
    if (currentState.warehousing[i].url === actionPayload.url) {
      currentState.warehousing[i].metadata = actionPayload.metadata;
    }
  }

  for (let i = 0; i < currentState.whReceipt.length; ++i) {
    if (currentState.whReceipt[i].url === actionPayload.url) {
      currentState.whReceipt[i].metadata = actionPayload.metadata;
    }
  }

  for (let i = 0; i < currentState.customer.length; ++i) {
    if (currentState.customer[i].url === actionPayload.url) {
      currentState.customer[i].metadata = actionPayload.metadata;
    }
  }

  for (let i = 0; i < currentState.utility.length; ++i) {
    if (currentState.utility[i].url === actionPayload.url) {
      currentState.utility[i].metadata = actionPayload.metadata;
    }
  }

  for (let i = 0; i < currentState.billings.length; ++i) {
    if (currentState.billings[i].url === actionPayload.url) {
      currentState.billings[i].metadata = actionPayload.metadata;
    }
  }

  for (let i = 0; i < currentState.myEvent.length; ++i) {
    if (currentState.myEvent[i].url === actionPayload.url) {
      currentState.myEvent[i].metadata = actionPayload.metadata;
    }
  }

  for (let i = 0; i < currentState.businessIntell.length; ++i) {
    if (currentState.businessIntell[i].url === actionPayload.url) {
      currentState.businessIntell[i].metadata = actionPayload.metadata;
    }
  }

  for (let i = 0; i < currentState.ams.length; ++i) {
    if (currentState.ams[i].url === actionPayload.url) {
      currentState.ams[i].metadata = actionPayload.metadata;
    }
  }

  for (let i = 0; i < currentState.isf.length; ++i) {
    if (currentState.isf[i].url === actionPayload.url) {
      currentState.isf[i].metadata = actionPayload.metadata;
    }
  }

  for (let i = 0; i < currentState.aes.length; ++i) {
    if (currentState.aes[i].url === actionPayload.url) {
      currentState.aes[i].metadata = actionPayload.metadata;
    }
  }

  for (let i = 0; i < currentState.myTask.length; ++i) {
    if (currentState.myTask[i].url === actionPayload.url) {
      currentState.myTask[i].metadata = actionPayload.metadata;
    }
  }

  for (let i = 0; i < currentState.cntr.length; ++i) {
    if (currentState.cntr[i].url === actionPayload.url) {
      currentState.cntr[i].metadata = actionPayload.metadata;
    }
  }

    for (let i = 0; i < currentState.shipmentStatus.length; ++i) {
    if (currentState.shipmentStatus[i].url === actionPayload.url) {
      currentState.shipmentStatus[i].metadata = actionPayload.metadata;
    }
  }

  for (let i = 0; i < currentState.aiCenter.length; ++i) {
    if (currentState.aiCenter[i].url === actionPayload.url) {
      currentState.aiCenter[i].metadata = actionPayload.metadata;
    }
  }

  return currentState;
}

function replaceUrlParentOpOnStack(currentState:Draft<ScreenStackState>, actionPayload:ReplaceUrlParentOpOnStack) {
  const findUrl = actionPayload.url.slice(0, actionPayload.url.indexOf('?parentOp='));

  const currentTime = nowMillisecond();
  
  const oldUrlOe = currentState.oceanExport.find(x => x.url.startsWith(findUrl))?.url;
  if(oldUrlOe) purgeScreenFromMemory([oldUrlOe]);
  // for quotation
  for (let i = 0; i < currentState.oceanExport.length; ++i) {
    if (currentState.oceanExport[i].url === oldUrlOe) {
      currentState.oceanExport[i].url = actionPayload.url;
      currentState.oceanExport[i].metadata = actionPayload.metadata;
      currentState.oceanExport[i].timestamp = currentTime;
    }
  }

  
  const oldUrlAe = currentState.airExport.find(x => x.url.startsWith(findUrl))?.url;
  if(oldUrlAe) purgeScreenFromMemory([oldUrlAe]);
  // for quotation
  for (let i = 0; i < currentState.airExport.length; ++i) {
    if (currentState.airExport[i].url === oldUrlAe) {
      currentState.airExport[i].url = actionPayload.url;
      currentState.airExport[i].metadata = actionPayload.metadata;
      currentState.airExport[i].timestamp = currentTime;
    }
  }
  
  const oldUrlAc = currentState.accounting.find(x => x.url.startsWith(findUrl))?.url;
  if(oldUrlAc) purgeScreenFromMemory([oldUrlAc]);
  // for invoice, bill, crdb
  for (let i = 0; i < currentState.accounting.length; ++i) {
    if (currentState.accounting[i].url === oldUrlAc) {
      currentState.accounting[i].url = actionPayload.url;
      currentState.accounting[i].metadata = actionPayload.metadata;
      currentState.accounting[i].timestamp = currentTime;
    }
  }
  
}

/**
 * Slice for controlling Screen Stack
 */
const screenStackSlice = createSlice({
  name: 'screenStack',
  initialState,
  reducers: {
    clearScreenStack: () => _.cloneDeep(initialState),
    pushScreenToStack: (state, action: PayloadAction<PushScreenToStack>) => {
     pushToStack(state, action.payload)
    },
    replaceScreenUrlOnStack: (state, action: PayloadAction<ReplaceUrlOnStack>) => {
      replaceUrlOnStack(state, action.payload);
    },
    popScreenFromStack: (state, action: PayloadAction<PopScreenFromStack>) => {
      if (action.payload.activeOperation) {
        popScreenFromSection(state[action.payload.activeOperation], action.payload.url);
      } else {
        popScreenFromSection(state.oceanImport, action.payload.url);
        popScreenFromSection(state.oceanExport, action.payload.url);
        popScreenFromSection(state.airImport, action.payload.url);
        popScreenFromSection(state.airExport, action.payload.url);
        popScreenFromSection(state.otherShipment, action.payload.url);
        popScreenFromSection(state.accounting, action.payload.url);
        popScreenFromSection(state.warehousing, action.payload.url);
        popScreenFromSection(state.whReceipt, action.payload.url);
        popScreenFromSection(state.customer, action.payload.url);
        popScreenFromSection(state.utility, action.payload.url);
        popScreenFromSection(state.standalone, action.payload.url);
        popScreenFromSection(state.billings, action.payload.url);
        popScreenFromSection(state.myEvent, action.payload.url);
        popScreenFromSection(state.businessIntell, action.payload.url);
        popScreenFromSection(state.ams, action.payload.url);
        popScreenFromSection(state.isf, action.payload.url);
        popScreenFromSection(state.shipmentStatus, action.payload.url);
        popScreenFromSection(state.cntr, action.payload.url);
      }
    },
    replaceScreenStackMetadata: (state, action: PayloadAction<ReplaceScreenStackMetadata>) => {
      replaceUrlMetadata(state, action.payload);
    },
    moveScreenToTop: (state, action: PayloadAction<MoveScreenToTop>) => {
      if (action.payload.url.indexOf('parentOp') > -1) {
        moveScreenToTopForSection(state.accounting, action.payload.url);    // for inv, bill, crdb
        moveScreenToTopForSection(state.oceanExport, action.payload.url);   // for quotation
        moveScreenToTopForSection(state.airExport, action.payload.url);     // for quotation
      } else {
        moveScreenToTopForSection(state[action.payload.operation], action.payload.url);
      }
    },
    moveScreenToOperation: (state, action: PayloadAction<MoveScreenToOperation>) => {
      doMoveScreenToOperation(state, action.payload.url, action.payload.sourceOperation, action.payload.destinationOperation);
    },
    replaceScreenUrlParentOpOnStack: (state, action: PayloadAction<ReplaceUrlParentOpOnStack>) => {
      replaceUrlParentOpOnStack(state, action.payload);
    },
  }
});

export default screenStackSlice.reducer;

export const {
  clearScreenStack,
  pushScreenToStack,
  replaceScreenUrlOnStack,
  popScreenFromStack,
  replaceScreenStackMetadata,
  moveScreenToTop,
  moveScreenToOperation,
  replaceScreenUrlParentOpOnStack
} = screenStackSlice.actions;
